import {
    BannerRow,
    ColCheckboxSwitch,
    ColCheckboxSwitchHead,
    ColumnTooltip,
    DelayedLoadingMessage,
    NoDataRow,
    SortIcon,
    SortIndicator,
    Table,
    TableBody,
    TableCol,
    TableHead,
    TableHeadCol,
    TableHeadColFilter,
    TableHeadColFilterUntracked,
    TableRowBody,
    TableRowHead,
    TableRowParent,
    VirtualRow,
} from './styledComponents';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import Drawer from 'components/Drawer';
import { FiTrash2 } from 'react-icons/fi';
import { IoIosArrowBack } from 'react-icons/io';
import { PlaceholderText } from 'components/Placeholders';
import { SoftButton } from '../../Button';
import TableHeadColSearch from './TableHeadColSearch';
import { UnstyledButton } from 'utils/fragments';
import isEqual from 'react-fast-compare';
import { mixpanelTrack } from 'utils/app';
import throttle from 'lodash/throttle';
import { useVirtualizer } from '@tanstack/react-virtual';

const CHECK_WIDTH = 3.125;
const REMOVE_WIDTH = 3.125;

function getMinTableWidth({ columns, onToggleCheck, onRemoveItem }) {
    let totalMinColumnWidth = columns.reduce(
        (sum, { $minWidth }) => sum + $minWidth,
        0,
    );
    if (onToggleCheck) totalMinColumnWidth += CHECK_WIDTH;
    if (onRemoveItem) totalMinColumnWidth += REMOVE_WIDTH;
    return totalMinColumnWidth;
}

function DataTableHead({
    data,
    columns,
    sortOption,
    setSortOption,
    onSearchInputChange,
    onRemoveItem,
    onToggleCheck,
    name,
    TableHeadColFilter,
    week,
    year,
    $minWidth,
    limitSearchWords,
}) {
    const { sortBy, ascending } = sortOption;
    const getToggleSort = (defaultSortAscending, key) => (e) => {
        setSortOption({
            sortBy: key,
            ascending: sortBy === key ? !ascending : !!defaultSortAscending,
        });
    };

    return (
        <TableRowHead $minWidth={$minWidth}>
            {onToggleCheck ? (
                <ColCheckboxSwitchHead
                    data={data}
                    onToggleCheck={onToggleCheck}
                    $minWidth={CHECK_WIDTH}
                    $width={CHECK_WIDTH}
                />
            ) : null}
            {columns.map(
                ({
                    key,
                    label,
                    $minWidth,
                    $width,
                    tooltip,
                    mixpanelContentKey,
                    sortable = true,
                }) => {
                    const active = sortBy === key;
                    const defaultSortAscending = key === 'parentName';

                    if (key === 'name') {
                        return (
                            <TableHeadColSearch
                                onSearchInputChange={onSearchInputChange}
                                name={name}
                                mixpanelMeta={{ contentKey: key }}
                                key={`thead-${key}`}
                                $minWidth={$minWidth}
                                $width={$width}
                                limitSearchWords={limitSearchWords}
                                showSearchMessage={true}
                            />
                        );
                    }

                    const TableHeadColFilterComponent = sortable
                        ? TableHeadColFilter
                        : TableHeadColFilterUntracked;

                    return (
                        <ColumnTooltip
                            key={`thead-${key}`}
                            week={week}
                            year={year}
                            tooltip={tooltip}
                            mixpanelContentKey={mixpanelContentKey}
                        >
                            <TableHeadColFilterComponent
                                as={sortable ? UnstyledButton : 'span'}
                                active={active}
                                colKey={key}
                                isDefaultOrder={
                                    defaultSortAscending === ascending
                                }
                                {...(sortable && {
                                    onClick: getToggleSort(
                                        defaultSortAscending,
                                        key,
                                    ),
                                })}
                                mixpanelMeta={{
                                    contentKey: key,
                                    ascending: active
                                        ? !ascending
                                        : defaultSortAscending,
                                }}
                                $minWidth={$minWidth}
                                $width={$width}
                            >
                                {sortable ? (
                                    <SortIndicator
                                        active={active}
                                        ascending={ascending}
                                    />
                                ) : null}
                                <span>{label || key}</span>
                                {sortable ? <SortIcon /> : null}
                            </TableHeadColFilterComponent>
                        </ColumnTooltip>
                    );
                },
            )}
            {onRemoveItem ? (
                <TableHeadCol $minWidth={REMOVE_WIDTH} colKey="remove" />
            ) : null}
        </TableRowHead>
    );
}

function Cell({
    column,
    node,
    selected,
    active,
    TableCol: TableColComp = TableCol,
    $minWidth,
    $width,
}) {
    const { key, formatCell } = column;
    const value = node[key];
    const { fetching, fetchingData } = node;
    const isName = key === 'name' || key === 'nameWithRowCount';
    const isCluster = key === 'cluster';

    if (!value && value !== 0 && (fetching || fetchingData)) {
        return (
            <TableColComp $width={$width} $minWidth={$minWidth} colKey={key}>
                <PlaceholderText
                    width={isName ? '9rem' : isCluster ? '1.5rem' : '3rem'}
                />
            </TableColComp>
        );
    }

    return (
        <TableColComp
            $minWidth={$minWidth}
            $width={$width}
            active={active}
            selected={selected}
            colKey={key}
            {...(isName && value && value.length > 30 && { title: value })}
        >
            {formatCell ? (
                formatCell({ key, value, row: node, active, selected })
            ) : (
                <span>{value}</span>
            )}
        </TableColComp>
    );
}

function Row({
    columns,
    node,
    height,
    index,
    selected,
    onRowClick,
    maxSortMetric,
    parentNode,
    sortOption,
    onRemoveItem,
    onToggleCheck,
    TableCol: TableColComp = TableCol,
    RowComponent = TableRowBody,
    $start,
    $minWidth,
    dataTableIsFetching,
}) {
    const { fetching, fetchingData } = node;
    const hasOnClick =
        !(fetching || fetchingData || dataTableIsFetching) && onRowClick;
    const onClick = hasOnClick && ((e) => onRowClick(e, node));
    return (
        <RowComponent
            height={height}
            index={index}
            parentNode={parentNode}
            selected={selected}
            $start={$start}
            $minWidth={$minWidth}
            {...(onClick && { onClick, as: 'button' })}
        >
            {onToggleCheck ? (
                <ColCheckboxSwitch
                    disabled={!(node.timeSeries && node.timeSeries.length)}
                    checked={!!node.checked}
                    onChange={(checked) => onToggleCheck({ node, checked })}
                    color={node.color}
                    $minWidth={CHECK_WIDTH}
                />
            ) : null}
            {columns.map((column) => (
                <Cell
                    column={column}
                    key={`${column.key}-${node.id}`}
                    node={node}
                    selected={selected}
                    sortBy={sortOption.sortBy}
                    maxSortMetric={maxSortMetric}
                    active={sortOption.sortBy === column.key}
                    TableCol={TableColComp}
                    $minWidth={column.$minWidth}
                    $width={column.$width}
                />
            ))}
            {onRemoveItem ? (
                <TableColComp $minWidth={REMOVE_WIDTH} colKey="remove">
                    <SoftButton
                        size="sm"
                        css={`
                            font-size: 1rem;
                            color: ${({ theme }) => theme.gray_4};
                        `}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveItem(node);
                        }}
                        mixpanelContext="RemoveItem"
                    >
                        <FiTrash2 />
                    </SoftButton>
                </TableColComp>
            ) : null}
        </RowComponent>
    );
}

function noop() {}
function useHandleScrollPositionNearBottom({ scrollElement, cb = noop }) {
    const onScroll = useCallback(
        throttle((e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            const isNearBottom =
                scrollTop + clientHeight >= scrollHeight * 0.65;
            if (isNearBottom) {
                cb();
            }
        }, 1000),
        [cb],
    );
    useEffect(() => {
        if (!scrollElement) return;
        scrollElement.addEventListener('scroll', onScroll);
        return () => {
            scrollElement.removeEventListener('scroll', onScroll);
        };
    }, [scrollElement, onScroll]);
}

function DataTable({
    columns,
    data,
    dataKey,
    maxSortMetric,
    sortOption,
    setSortOption,
    rowHeight = 46,
    renderDrawerContent,
    parentNode,
    onParentNodeClick,
    onRowClick,
    noDataMessage = 'No data to display',
    onSearchInputChange,
    onRemoveItem,
    onToggleCheck,
    DrawerComponent = Drawer,
    TableCol: TableColComponent = TableCol,
    TableHeadColFilter: TableHeadColFilterComponent = TableHeadColFilter,
    controlledSelectedId,
    controlledDrawerOpen,
    onDrawerChange,
    ...props
}) {
    const {
        name,
        week,
        year,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        isLoading,
        BannerRowContent,
        limitSearchWords,
    } = props;

    const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);
    const [internalSelectedId, setInternalSelectedId] = useState(null);

    // Use controlled props if provided, otherwise use internal state
    const drawerOpen =
        controlledDrawerOpen !== undefined
            ? controlledDrawerOpen
            : internalDrawerOpen;
    const selectedId =
        controlledSelectedId !== undefined
            ? controlledSelectedId
            : internalSelectedId;

    const updateDrawerState = (open, id) => {
        // Update internal state if not controlled
        if (controlledDrawerOpen === undefined) {
            setInternalDrawerOpen(open);
        }
        if (controlledSelectedId === undefined) {
            setInternalSelectedId(id);
        }
        // Notify parent if callback provided
        if (onDrawerChange) {
            onDrawerChange(open, id);
        }
    };

    const defaultOnRowClick = (e, node) => {
        if (selectedId !== node.id)
            mixpanelTrack('InfoDrawer', {
                name: node.name,
                type: node.type,
            });
        updateDrawerState(true, node.id);
    };

    const itemCount = data ? data.length : 1;

    const itemData = {
        data: data || [
            { id: 'LOADING-ROW', fetching: true, fetchingData: true },
        ],
        columns,
        sortOption,
        rowHeight,
        maxSortMetric,
        parentNode,
        onRemoveItem,
        onToggleCheck,
        onRowClick:
            typeof onRowClick === 'undefined' ? defaultOnRowClick : onRowClick,
        selectedId: drawerOpen && selectedId,
        TableCol: TableColComponent,
    };

    const listRef = useRef(null);

    const rowVirtualizer = useVirtualizer({
        count: itemCount,
        getScrollElement: () => listRef.current,
        estimateSize: () => rowHeight,
        overscan: 25,
    });

    const cb = useCallback(() => {
        if (isFetchingNextPage) return;
        if (fetchNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, isFetchingNextPage]);
    useHandleScrollPositionNearBottom({
        scrollElement: listRef.current,
        cb,
    });

    useEffect(() => {
        if (listRef.current && listRef.current.scrollTo) {
            listRef.current.scrollTo({ top: 0 });
        }
    }, [dataKey]);

    useEffect(() => {
        // Only reset drawer if not controlled
        if (
            controlledDrawerOpen === undefined &&
            controlledSelectedId === undefined
        ) {
            updateDrawerState(false, null);
        }
    }, [dataKey]);

    let parentColumns;
    if (parentNode) {
        const formatParentNameCell = ({ value }) => (
            <div
                css={`
                    color: ${({ theme }) => theme.gray_3};
                    font-weight: 500;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                `}
            >
                <IoIosArrowBack css="margin-right: .25rem;" />
                <span>{value}</span>
            </div>
        );
        parentColumns = [
            { ...columns[0], formatCell: formatParentNameCell },
            ,
            ...columns.slice(1),
        ];
    }

    const $minWidth = getMinTableWidth({
        columns,
        onToggleCheck,
        onRemoveItem,
    });

    return (
        <div>
            {!isFetchingNextPage && !isLoading && isFetching ? (
                <DelayedLoadingMessage
                    delay={1000}
                    message="Updating results..."
                />
            ) : null}
            <Table $maxHeight={props.$maxHeight} ref={listRef}>
                <TableHead $minWidth={$minWidth}>
                    <DataTableHead
                        data={data}
                        columns={columns}
                        setSortOption={setSortOption}
                        sortOption={sortOption}
                        onSearchInputChange={onSearchInputChange}
                        onRemoveItem={onRemoveItem}
                        onToggleCheck={onToggleCheck}
                        name={name}
                        TableHeadColFilter={TableHeadColFilterComponent}
                        week={week}
                        year={year}
                        $minWidth={$minWidth}
                        limitSearchWords={limitSearchWords}
                    />
                </TableHead>
                <TableBody
                    isFetching={!isFetchingNextPage && !isLoading && isFetching}
                >
                    {parentNode && (
                        <Row
                            node={parentNode}
                            columns={parentColumns}
                            height={rowHeight}
                            onRowClick={onParentNodeClick}
                            sortOption={sortOption}
                            TableCol={TableColComponent}
                            $minWidth={$minWidth}
                            RowComponent={TableRowParent}
                            dataTableIsFetching={isFetching}
                        />
                    )}
                    {itemCount < 1 ? (
                        <NoDataRow $minWidth={$minWidth} height={rowHeight}>
                            <div>{noDataMessage}</div>
                        </NoDataRow>
                    ) : (
                        <div
                            css="position: relative;"
                            style={{
                                height: `${rowVirtualizer.getTotalSize()}px`,
                            }}
                        >
                            {rowVirtualizer
                                .getVirtualItems()
                                .map((virtualRow) => {
                                    const {
                                        data,
                                        rowHeight,
                                        selectedId,
                                        ...restData
                                    } = itemData;
                                    const node = data[virtualRow.index];
                                    return (
                                        <Row
                                            node={node}
                                            height={virtualRow.size}
                                            index={virtualRow.index}
                                            selected={node.id === selectedId}
                                            $start={virtualRow.start}
                                            $minWidth={$minWidth}
                                            key={virtualRow.index}
                                            RowComponent={VirtualRow}
                                            dataTableIsFetching={isFetching}
                                            {...restData}
                                        />
                                    );
                                })}
                        </div>
                    )}
                    {BannerRowContent && data?.length ? (
                        <BannerRow $minWidth={$minWidth} height={rowHeight}>
                            {BannerRowContent}
                        </BannerRow>
                    ) : null}
                    {isFetchingNextPage ? (
                        <Row
                            node={{
                                id: 'LOADING-ROW',
                                fetching: true,
                                fetchingData: true,
                            }}
                            height={rowHeight}
                            columns={columns}
                            sortOption={sortOption}
                            rowHeight={rowHeight}
                            maxSortMetric={maxSortMetric}
                            parentNode={parentNode}
                            onRemoveItem={onRemoveItem}
                            onToggleCheck={onToggleCheck}
                        />
                    ) : null}
                    {renderDrawerContent && (
                        <DrawerComponent
                            open={drawerOpen}
                            onClose={() => updateDrawerState(false, null)}
                        >
                            {renderDrawerContent(selectedId)}
                        </DrawerComponent>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

export default memo(DataTable, isEqual);
